// Portal authenticators — the authenticator endpoints
// (backend/app/routes/portal_authenticators.py), in one place. Contract:
// docs/portal-authenticators.md.
//
// Every field the card reads is named here and nowhere else. Nothing this module
// sends or reads is kept: the pasted key goes straight out in the enrol body and
// is not retained; a revealed code goes straight to the caller's state for the
// seconds it is valid. No console line, no storage.

import { post, request } from "./api";

const API_URL = process.env.REACT_APP_API_URL || "";
const base = (practiceId) => `${API_URL}/api/practices/${practiceId}/authenticators`;

export function normalizeAuthenticator(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ?? null,
    portalKey: raw.portal_key || null,
    portalLabel: raw.portal_label || raw.portal_key || null,
    label: raw.label || "",
    digits: typeof raw.digits === "number" ? raw.digits : 6,
    periodSeconds: typeof raw.period_seconds === "number" ? raw.period_seconds : 30,
    createdBy: raw.created_by ?? null,
    createdByEmail: raw.created_by_email || null,
    createdAt: raw.created_at || null,
    revokedAt: raw.revoked_at || null,
    lastUsedAt: raw.last_used_at || null,
  };
}

// GET …/authenticators → {enabled, portals, authenticators}
export async function fetchAuthenticators(practiceId) {
  const body = await request(base(practiceId), {}, "Could not load the authenticators.");
  return {
    enabled: body.enabled === true,
    portals: Array.isArray(body.portals)
      ? body.portals
          .filter((p) => p && typeof p.key === "string")
          .map((p) => ({ key: p.key, label: p.label || p.key }))
      : [],
    authenticators: Array.isArray(body.authenticators)
      ? body.authenticators.map(normalizeAuthenticator).filter(Boolean)
      : [],
  };
}

// POST …/authenticators {portal, label, secret} → 201 {authenticator}. The secret
// is whatever was pasted (the key or the otpauth URI); it leaves here once.
export async function enrollAuthenticator(practiceId, { portal, label, secret }) {
  const body = await request(
    base(practiceId),
    post({ portal, label, secret }),
    "Could not enrol the authenticator.",
  );
  return normalizeAuthenticator(body.authenticator);
}

// DELETE …/authenticators/<portal> → {authenticator}
export async function revokeAuthenticator(practiceId, portalKey) {
  const body = await request(
    `${base(practiceId)}/${encodeURIComponent(portalKey)}`,
    { method: "DELETE" },
    "Could not revoke the authenticator.",
  );
  return normalizeAuthenticator(body.authenticator);
}

// GET …/authenticators/<portal>/code → {code, seconds_remaining, ...}
export async function fetchAuthenticatorCode(practiceId, portalKey) {
  const body = await request(
    `${base(practiceId)}/${encodeURIComponent(portalKey)}/code`,
    {},
    "Could not get the code.",
  );
  return {
    code: typeof body.code === "string" ? body.code : "",
    secondsRemaining:
      typeof body.seconds_remaining === "number" && body.seconds_remaining > 0
        ? Math.floor(body.seconds_remaining)
        : 0,
  };
}

// POST …/authenticators/<portal>/type-into-session → {typed: true}. The code is
// typed into the remote browser server-side; nothing about it comes back.
export async function typeAuthenticatorIntoSession(practiceId, portalKey) {
  const body = await request(
    `${base(practiceId)}/${encodeURIComponent(portalKey)}/type-into-session`,
    post({}),
    "Could not type the code into the session.",
  );
  return { typed: body.typed === true };
}
